import React from "react";
import Container from "@mui/material/Container";
import { styled, SxProps, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { EmtTypography } from "./EmtTypography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { EmtTheme } from "../types";
import { Breakpoint } from "@mui/system";
import { EmtIcon } from "./EmtIcon";

export type EmtSimpleLayoutProps = {
  title?: React.ReactNode;

  children?: React.ReactNode;
  /**
   * Logo src
   * @default undefined
   * @example './theme/logo.png'
   */
  logo?: React.ReactNode;
  /**
   * Gap between every blocs in children props
   * @default 1.5rem
   */
  elementsGap?: string;
  /**
   * Content top right
   * @default undefined
   */
  rightContent?: React.ReactNode;
  /**
   * Top Background of the page
   */
  topBackground?: string;
  /**
   * Font size for Desktop
   * @default 2.125rem
   */
  titleFontSize?: string;
  /**
   * Set margin bottom for mobile
   * @default true
   */
  mobileMarginBottom?: boolean;
  sx?: SxProps;
  className?: string;
  nonce?: string;
  pro?: boolean;
  maxWidth?: Breakpoint;
  goBack?: () => void;
};

type WrapperProps = {
  theme?: EmtTheme;
  topBackground: string;
  mobileMarginBottom: boolean;
  pro?: boolean;
};

const Wrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "topBackground" && prop !== "mobileMarginBottom" && prop !== "pro",
})((props: WrapperProps) => {
  const { theme, topBackground = "", mobileMarginBottom, pro = false } = props;
  return {
    background: `url('${topBackground}'), linear-gradient(180deg, ${
      pro ? theme?.palette.primary.dark : theme?.palette.primary.main
    } 270px, transparent 270px)`,
    backgroundSize: "60%",
    paddingTop: "40px",
    "& #simple-layout-image-container img": {
      maxWidth: "190px",
      [theme?.breakpoints.down("md")]: {
        maxWidth: "145px",
      },
    },
    [theme?.breakpoints.down("md")]: {
      backgroundSize: "130%",
      marginBottom: mobileMarginBottom ? "90px" : 0,
    },
  };
});

const ChildrenBox = styled(Box)(({ theme }) => {
  return {
    borderRadius: "16px",
    backgroundColor: theme.palette.background.default,
    padding: "27px",
    marginTop: "50px",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("md")]: {
      marginTop: "30px",
      padding: "20px",
    },
  };
});

export function EmtSimpleLayout(props: EmtSimpleLayoutProps) {
  const {
    children,
    title,
    logo,
    elementsGap = "1.2rem",
    titleFontSize = "2.125rem",
    rightContent,
    topBackground,
    sx,
    mobileMarginBottom = true,
    pro = false,
    className,
    maxWidth,
    goBack,
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <Wrapper
      className={className}
      topBackground={topBackground as string}
      mobileMarginBottom={mobileMarginBottom as any}
      sx={sx}
      pro={pro}
    >
      <Container
        maxWidth={maxWidth || "sm"}
        disableGutters={isMobile ? true : false}
      >
        <Box
          id="simple-layout-image-container"
          className="EmtReactNativeWebViewMarginTop"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: isMobile ? "0 20px" : 0,
          }}
        >
          <>
            {logo && logo}
            {rightContent && rightContent}
          </>
        </Box>

        <ChildrenBox sx={{ gap: elementsGap }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
            {goBack && (
              <Box
                onClick={() => goBack()}
                sx={{
                  position: "relative",
                  width: 48,
                  height: 48,
                  borderRadius: "18px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  background: "#fff",
                  cursor: "pointer",
                  boxShadow: "0px 8px 12px 0px #00000014",
                  flexShrink: 0,
                }}
              >
                <EmtIcon
                  className="emt-arrow-left"
                  sx={{
                    color: "purple.dark",
                    fontSize: "20px",
                  }}
                />
              </Box>
            )}
            {title && (
              <EmtTypography
                variant="h1"
                sx={{ fontSize: isMobile ? "1.5rem" : [titleFontSize] }}
              >
                <>{title}</>
              </EmtTypography>
            )}
          </Box>
          <>{children}</>
        </ChildrenBox>
      </Container>
    </Wrapper>
  );
}
